import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { Check, ChevronDown, CircleAlert, History, RefreshCw, Sparkles } from 'lucide-react';
import { Badge, IconChip } from '../design/ui';
import { fmtDateTime, compactJson, prettyJson, toolLabel } from './aiTrace/format';
import './AiProposalsPanel.css';

// The proposal ledger of a ticket (backend plan H2): every effectful tool call
// of the AI is a typed proposal — pending (waiting for an operator, or a
// deferred effect applied with the reply), committed, refused, failed, expired.
// Pending human proposals are decidable cards (kit § « Propositions de
// l'IA »); the rest is the history, folded.

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });

const STATUS_ORDER = ['pending', 'committed', 'failed', 'refused', 'expired'];
const STATUS_TONES = { committed: 'success', failed: 'danger', pending: 'info' };

const AiProposalsPanel = ({ ticketId, onChanged }) => {
    const { t, i18n } = useTranslation();
    const [proposals, setProposals] = useState(null);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(null);
    const [showHistory, setShowHistory] = useState(false);
    const [openDetails, setOpenDetails] = useState({});

    const load = useCallback(async () => {
        if (!ticketId) return;
        setError(null);
        try {
            const response = await axios.get(`/api/tickets/${ticketId}/ai-proposals`, authHeaders());
            setProposals(response.data.proposals || []);
        } catch (e) {
            setProposals([]);
            setError(t('aiProposals.loadError', 'Impossible de charger les propositions.'));
        }
    }, [ticketId, t]);

    useEffect(() => { load(); }, [load]);

    const decide = async (proposal, action) => {
        setBusy(proposal.id);
        setError(null);
        try {
            const body = action === 'refuse' ? { reason: '' } : {};
            await axios.post(`/api/ai-proposals/${proposal.id}/${action}`, body, authHeaders());
            await load();
            if (onChanged) onChanged();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : t('aiProposals.decideError', 'La décision n’a pas pu être appliquée.'));
        } finally {
            setBusy(null);
        }
    };

    const isHumanPending = (p) => p.status === 'pending' && p.decision?.mode === 'human';
    const statusLabel = (p) => {
        if (p.status === 'pending' && p.decision?.mode !== 'human') return t('aiProposals.status.deferred', 'appliqué avec la réponse');
        return t(`aiProposals.status.${p.status}`, p.status);
    };
    const toggleDetail = (id) => setOpenDetails((prev) => ({ ...prev, [id]: !prev[id] }));

    // The rule that decided, who decided, the error: one quiet line.
    const metaLine = (p) => [
        p.decision?.rule && `${t('aiProposals.rule', 'règle')} : ${p.decision.rule}${p.decision.reason ? ` — ${p.decision.reason}` : ''}`,
        p.decided_by?.type && p.status !== 'pending' && `${t('aiProposals.decidedBy', 'décidé par')} ${p.decided_by.type}`,
    ].filter(Boolean).join(' · ');

    // The raw payload stays behind « Voir le détail ».
    const renderDetail = (p) => {
        const hasPayload = p.payload && Object.keys(p.payload).length > 0;
        if (!hasPayload) return null;
        const open = Boolean(openDetails[p.id]);
        return (
            <>
                <button type="button" className="apr-detail-toggle" onClick={() => toggleDetail(p.id)} aria-expanded={open}>
                    <ChevronDown className="apr-detail-chevron" strokeWidth={2} aria-hidden="true" />
                    {open ? t('ticketWorkspaceUi.ai.hideDetail', 'Masquer le détail') : t('ticketWorkspaceUi.ai.showDetail', 'Voir le détail')}
                </button>
                {open && <pre className="apr-json">{prettyJson(p.payload)}</pre>}
            </>
        );
    };

    const renderPending = (p) => (
        <article key={p.id} className="apr-card">
            <div className="apr-card__head">
                <IconChip icon={Sparkles} size="sm" />
                <div className="apr-card__heading">
                    <div className="apr-card__title">
                        {toolLabel(p.tool, t)}
                        <Badge tone="warning" dot>{t('ticketWorkspaceUi.ai.toValidate', 'À valider')}</Badge>
                    </div>
                    {p.explain && <p className="apr-card__explain">{p.explain}</p>}
                    <p className="apr-card__meta">
                        {[fmtDateTime(p.created_at, i18n.language), metaLine(p)].filter(Boolean).join(' · ')}
                    </p>
                    {renderDetail(p)}
                </div>
            </div>
            <div className="apr-card__actions">
                <button type="button" className="u-btn u-btn--secondary u-btn--sm" disabled={busy === p.id} onClick={() => decide(p, 'refuse')}>
                    {t('aiProposals.refuse', 'Refuser')}
                </button>
                <button type="button" className="u-btn u-btn--primary u-btn--sm" disabled={busy === p.id} onClick={() => decide(p, 'approve')}>
                    <Check strokeWidth={2} aria-hidden="true" />
                    {t('aiProposals.approve', 'Approuver et exécuter')}
                </button>
            </div>
        </article>
    );

    const renderHistoryItem = (p) => (
        <li key={p.id} className={`apr-row apr-row--${p.status}`}>
            <div className="apr-row__main">
                <div className="apr-row__title">
                    <span className="apr-row__tool">{toolLabel(p.tool, t)}</span>
                    <Badge tone={p.status === 'pending' ? 'info' : STATUS_TONES[p.status]}>{statusLabel(p)}</Badge>
                </div>
                {p.explain && <p className="apr-row__explain">{p.explain}</p>}
                <p className="apr-row__meta">
                    {[fmtDateTime(p.created_at, i18n.language), metaLine(p)].filter(Boolean).join(' · ')}
                </p>
                {p.error && (
                    <p className="apr-row__error">
                        <CircleAlert strokeWidth={1.8} aria-hidden="true" />
                        {p.error.type}: {compactJson(p.error.message, 160)}
                    </p>
                )}
                {renderDetail(p)}
            </div>
        </li>
    );

    const pending = (proposals || []).filter(isHumanPending);
    const history = (proposals || []).filter((p) => !isHumanPending(p))
        .sort((a, b) => STATUS_ORDER.indexOf(a.status) - STATUS_ORDER.indexOf(b.status) || (b.created_at || '').localeCompare(a.created_at || ''));

    return (
        <section className="ai-proposals-panel apr" aria-labelledby="apr-title">
            <div className="apr-head">
                <h3 id="apr-title" className="apr-head__title">
                    {t('aiProposals.title', 'Propositions de l’IA')}
                    {proposals && <span className="apr-head__count">{proposals.length}</span>}
                    {pending.length > 0 && (
                        <Badge tone="warning">{pending.length} {t('aiProposals.awaiting', 'à valider')}</Badge>
                    )}
                </h3>
                <button
                    type="button"
                    className="u-btn u-btn--ghost u-btn--icon u-btn--sm"
                    onClick={load}
                    title={t('aiProposals.refresh', 'Actualiser')}
                    aria-label={t('aiProposals.refresh', 'Actualiser')}
                >
                    <RefreshCw strokeWidth={1.8} />
                </button>
            </div>
            <p className="apr-intro">
                {t('aiProposals.intro', 'Chaque action de l’IA sur ce ticket est une proposition : exécutée aussitôt, appliquée avec la réponse, ou en attente de votre validation selon la politique du compte.')}
            </p>
            {error && (
                <div className="u-banner u-banner--danger" role="alert">
                    <CircleAlert strokeWidth={2} aria-hidden="true" />
                    <div className="u-banner__body">{error}</div>
                </div>
            )}
            {proposals === null && !error && (
                <div className="apr-skeleton" aria-busy="true">
                    <span className="u-skeleton apr-skeleton__line" />
                    <span className="u-skeleton apr-skeleton__block" />
                </div>
            )}
            {proposals && proposals.length === 0 && !error && (
                <p className="apr-empty">{t('aiProposals.empty', 'Aucune proposition enregistrée pour ce ticket.')}</p>
            )}
            {pending.map(renderPending)}
            {history.length > 0 && (
                <>
                    <button type="button" className="apr-toggle" onClick={() => setShowHistory((v) => !v)} aria-expanded={showHistory}>
                        <History strokeWidth={1.8} aria-hidden="true" />
                        {showHistory ? t('aiProposals.hideHistory', 'Masquer l’historique') : t('aiProposals.showHistory', 'Historique ({{count}})', { count: history.length })}
                    </button>
                    {showHistory && <ul className="apr-list">{history.map(renderHistoryItem)}</ul>}
                </>
            )}
        </section>
    );
};

export default AiProposalsPanel;
